import { FC, useEffect, useMemo, useRef, useState } from "react";
import loadable from "@loadable/component";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import CustomLink from "@Components/CustomLink";
import InlineLottie from "@Components/InlineLottie/InlineLottie";
import Image from "@Components/Image";
import LoginDispatcher from "@Components/Login/actions";
import { logoutRequest } from "@Components/Login/components/helper";
import Modal from "@Components/Modal";
import Portal from "@Components/Portal";
import config from "@Config/index";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import currencyData from "constants/currencyConstants";
import { COMPONENTS_CATEGORY, EVENT_LABEL } from "constants/index";
import {
  DASHBOARD_URL,
  GROUP_BOOKINGS_URL,
  LEADS_LIST,
  PARTNER_WITH_AMBER_URL,
  PROFILE_LEADS_URL,
  PROFILE_SHORTLIST_URL,
  PROFILE_URL,
  PAGE,
  REFER_URL,
} from "constants/configConstants";
import { isInternalUser } from "helpers/auth";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useLanguagePath } from "hooks/useLanguagePath";
import useTranslationHook from "hooks/useTranslationHook";
import { languagesForAmberUserWebsite } from "locales/langConfig/langConfig";

import listIllustration from "../../assets/list-building.png";
// A 3D handshake (supplied), one navy and one grey cuff, beside the building illustration.
import partnerIllustration from "../../assets/partner-handshake.png";
import iconDownload from "../../assets/menu-download.json";
import navTranslate from "../../assets/nav-translate.json";
import type { MenuIcons } from "./menuIcons";
import useIsApp from "hooks/useIsApp";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import Chevron from "@Icons/Chevron";
import ProductsMenu from "./ProductsMenu";
import PreferencesModal from "./PreferencesModal";
import { avatarOf, initialsOf, nameOf, phoneOf } from "../../accountContact";
import useCopy from "../../useCopy";
import styles from "./HomeV2HeroNav.module.scss";

// Load on demand — a static import pulls FooterV2/constant while configConstants is still init (SSR crash).
const AppDownloadModal = loadable(() => import("@Components/SiteFooter/AppDownloadModal"));
// Same cycle via CallUsDrawer → PHONE_* at module init when SiteHeaderBar SSRs ScrolledHeader early.
const CallUsDrawer = loadable(() => import("../Support/CallUsDrawer"));

/**
 * The hero header's right-hand cluster on HomeV2 — Wander's shape with Airbnb's menu:
 * a dark translucent "List with us" pill and a round menu button on the photograph; the
 * button opens a white rounded menu (and turns into a ×).
 *
 * The menu, top to bottom, in groups: sign in; your account (profile, bookings,
 * shortlist); for students (group booking, refer a friend, the app); settings and help
 * (languages & currency, Help Centre); and for property partners ("List your property"
 * and "Partner with us" together).
 *
 * Rendered into the shared `HeaderDesktop` through its opt-in `rightContent` slot. Every
 * item reuses a working part of the site rather than a new one: the login modal, the
 * Airbnb-style PreferencesModal (same language/currency behaviour as the shared picker),
 * the footer's scan-or-download app modal, the account menu's links and logout, and the
 * top nav's analytics events.
 */

// Only rendered once "Support" is clicked, so it — and its four Lottie icons — load then.
const SupportMenu = loadable(() => import("../Support/SupportMenu"));
// The phone's menu: the live site's own menu, in an iOS sheet. Fetched on first open.
const MenuSheet = loadable(() => import("../MenuSheet"));

/** One pass of an Iconly Motion icon: 90 frames at 60fps. */
const ICON_PASS_MS = 1500;

/**
 * A menu row's animated Iconly Motion ("Light") icon. It plays once each time the
 * pointer enters its ROW (not just the 20px icon) and won't restart mid-pass. The
 * artwork's colour is baked in (the menu's grey ink).
 */
const RowIcon: FC<{ data: unknown }> = ({ data }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [play, setPlay] = useState(false);
  useEffect(() => {
    const row = ref.current?.closest("a, button");
    if (!row) return undefined;
    let timer = 0;
    const onEnter = () => {
      if (timer) return;
      setPlay(true);
      timer = window.setTimeout(() => {
        timer = 0;
        setPlay(false);
      }, ICON_PASS_MS);
    };
    row.addEventListener("mouseenter", onEnter);
    return () => {
      row.removeEventListener("mouseenter", onEnter);
      window.clearTimeout(timer);
    };
  }, []);
  return (
    <span ref={ref} className={styles.rowIcon} aria-hidden="true">
      {/* Until the menu's icons have loaded (see menuIcons.ts), an empty 20px box holds
        the row's layout. */}
      {data ? (
        <InlineLottie data={data} size={20} autoPlay={false} loop={false} play={play} />
      ) : (
        <span style={{ display: "inline-block", height: 20, width: 20 }} />
      )}
    </span>
  );
};

/** Hamburger that morphs into a × when the menu is open. */
const MenuGlyph: FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={`${styles.glyph} ${open ? styles.glyphOpen : ""}`}
  >
    <path className={styles.bar1} d="M4 7h16" />
    <path className={styles.bar2} d="M4 12h16" />
    <path className={styles.bar3} d="M4 17h16" />
  </svg>
);

/** How long after mount the bar's icons play their first pass: once the hero has settled. */
const NAV_INTRO_DELAY_MS = 700;

/**
 * An animated Iconly Motion icon in the header bar ("Translate language", "question mark
 * circle"). It plays one pass shortly after the page loads — a quiet hint that the bar
 * is alive — and again each time the pointer enters its link, never restarting
 * mid-pass. The artwork is black; `.navIcon` in the stylesheet inks it white.
 */
const NavIcon: FC<{ data: unknown }> = ({ data }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [play, setPlay] = useState(false);
  useEffect(() => {
    const row = ref.current?.closest("a, button");
    let timer = 0;
    const pass = () => {
      if (timer) return;
      setPlay(true);
      timer = window.setTimeout(() => {
        timer = 0;
        setPlay(false);
      }, ICON_PASS_MS);
    };
    const intro = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? 0
      : window.setTimeout(pass, NAV_INTRO_DELAY_MS);
    row?.addEventListener("mouseenter", pass);
    return () => {
      row?.removeEventListener("mouseenter", pass);
      window.clearTimeout(intro);
      window.clearTimeout(timer);
    };
  }, []);
  return (
    <span ref={ref} className={styles.navIcon} aria-hidden="true">
      <InlineLottie data={data} size={20} autoPlay={false} loop={false} play={play} />
    </span>
  );
};

/**
 * "Support"'s headphones (the supplied `headphones-stroke-rounded.svg`), animated the way
 * the bar's Iconly icons are: one pass shortly after load and again on hover, never
 * restarting mid-pass. A pass redraws the band, then the two ear cups land with a small
 * overshoot — the Iconly "draw, then settle" motion, in CSS so the artwork stays the
 * supplied SVG.
 */
const SupportIcon: FC = () => {
  const ref = useRef<HTMLSpanElement>(null);
  const [play, setPlay] = useState(false);
  useEffect(() => {
    const row = ref.current?.closest("a, button");
    let timer = 0;
    const pass = () => {
      if (timer) return;
      setPlay(true);
      timer = window.setTimeout(() => {
        timer = 0;
        setPlay(false);
      }, ICON_PASS_MS);
    };
    const intro = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? 0
      : window.setTimeout(pass, NAV_INTRO_DELAY_MS);
    row?.addEventListener("mouseenter", pass);
    return () => {
      row?.removeEventListener("mouseenter", pass);
      window.clearTimeout(intro);
      window.clearTimeout(timer);
    };
  }, []);
  return (
    <span
      ref={ref}
      className={`${styles.navIcon} ${styles.supportIcon} ${play ? styles.supportIconPlay : ""}`}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path
          className={styles.supportBand}
          pathLength={1}
          d="M20.0849 17C20.5849 15.5 21 13.4368 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 13.4368 3.41512 15.5 3.91512 17"
        />
        <path
          className={`${styles.supportCup} ${styles.supportCupLeft}`}
          d="M8.97651 19.6043L7.23857 14.6127C7.05341 14.1466 6.4617 13.9131 5.97493 14.0297C4.46441 14.5333 3.6462 16.1718 4.14742 17.6895L4.58543 19.0158C5.08664 20.5334 6.71747 21.3555 8.22799 20.8519C8.68896 20.6556 9.10449 20.0897 8.97651 19.6043Z"
        />
        <path
          className={`${styles.supportCup} ${styles.supportCupRight}`}
          d="M15.0235 19.6043L16.7614 14.6127C16.9466 14.1466 17.5383 13.9131 18.0251 14.0297C19.5356 14.5333 20.3538 16.1718 19.8526 17.6895L19.4146 19.0158C18.9134 20.5334 17.2825 21.3555 15.772 20.8519C15.311 20.6556 14.8955 20.0897 15.0235 19.6043Z"
        />
      </svg>
    </span>
  );
};

/**
 * `tone`: "photo" (default) for the hero, where the controls sit on the photograph in
 * charcoal glass; "light" for the scrolled header's white bar, where the same controls
 * turn to hairline outlines with ink text.
 */
const HomeV2HeroNav: FC<{
  tone?: "photo" | "light";
  /** Hides the List/Partner feature tiles (desktop) and property-owner rows (phone menu). */
  hidePartnerMenuTiles?: boolean;
}> = ({ tone = "photo", hidePartnerMenuTiles = false }) => {
  const { t } = useTranslationHook();
  const copy = useCopy();
  const dispatch = useDispatch();
  const { getLocalizedPath } = useLanguagePath();
  const [menuOpen, setMenuOpen] = useState(false);
  // On a phone the menu is the live menu in a bottom sheet (MenuSheet), not this panel.
  const isDesktop = useSelector(({ appData }: AppState) => appData.isDesktop);
  // No "download the app" inside the app or the WeChat mini-program (as the old header).
  const isApp = useIsApp();
  const isMiniProgram = useIsMiniProgram();
  const showAppDownload = !isApp && !isMiniProgram;
  const [productsOpen, setProductsOpen] = useState(false);
  // The language picker's event as each old header sent it (P1D-5530): the desktop header
  // `language_currency_selector_clicked`, the phone menu `language_modal_clicked`.
  const languageEvent = isDesktop
    ? "language_currency_selector_clicked"
    : EVENT_LABEL.LANGUAGE_MODAL_CLICKED;
  // The menu panel's icons, loaded on first intent to open it (see menuIcons.ts).
  const [menuIcons, setMenuIcons] = useState<MenuIcons | null>(null);
  const menuIconsRequested = useRef(false);
  const loadMenuIcons = () => {
    if (menuIconsRequested.current) return;
    menuIconsRequested.current = true;
    import("./menuIcons")
      .then((m) => setMenuIcons(m.default))
      .catch(() => {
        // A failed chunk leaves the rows' placeholders; let the next hover try again.
        menuIconsRequested.current = false;
      });
  };
  useEffect(() => {
    if (menuOpen) loadMenuIcons();
  }, [menuOpen]);
  // The Support menu, opened from the bar's "Support" pill; and its "Call us" drawer.
  const [supportOpen, setSupportOpen] = useState(false);
  const [callUsOpen, setCallUsOpen] = useState(false);
  const [modal, setModal] = useState<"" | "preferences" | "app">("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const {
    isUserLoggedIn,
    userData,
    currencyExchangeRates,
    userLanguage,
    userCurrency,
    country,
    region,
  } = useSelector(
    ({ appData, searchPageData }: AppState) => ({
      country: appData.country,
      region: searchPageData.region,
      isUserLoggedIn: appData.isUserLoggedIn,
      userData: appData.userData,
      currencyExchangeRates: appData.currencyExchangeRates,
      userLanguage: appData.userLanguage,
      userCurrency: appData.userCurrency,
    }),
    shallowEqual,
  );

  // As the old desktop header: internal (amberstudent.com) users get the Products dropdown
  // (Dashboard, Amber Flex, Amber Connect) in place of "Get the app".
  const showProducts = isDesktop && isInternalUser(userData);

  // Language code plus the chosen currency ISO code (e.g. EN · INR).
  const languageCode = (userLanguage || "en").toUpperCase();
  const userCurrencyCode =
    currencyData
      .find((it) => it.standard_name?.toLocaleLowerCase() === userCurrency?.toLocaleLowerCase())
      ?.standard_name?.toUpperCase() || (userCurrency ? userCurrency.toUpperCase() : "");
  const languageName =
    languagesForAmberUserWebsite.find((l) => l.code === (userLanguage || "en"))?.nativeLabel ||
    "English";

  // The referral bonus, converted exactly as the site's account menu does it: 50 GBP,
  // shown in the visitor's chosen currency when one is set.
  const referBonus = useMemo(() => {
    let amount = 50;
    let currency = "GBP";
    if (currencyExchangeRates?.baseCurrency) {
      const rate = currencyExchangeRates?.rates?.find(
        (r: any) => r.target_currency?.toLowerCase() === "gbp",
      );
      if (rate?.rate) {
        amount = Math.ceil(amount / rate.rate);
        currency = currencyExchangeRates.baseCurrency.toUpperCase();
      }
    }
    return `${amount} ${currency}`;
  }, [currencyExchangeRates?.baseCurrency]);

  const shortlistCount = userData?.data?.inventories_shortlists_ids?.length || 0;

  // Phones keep the old mobile header's label, `"search_page <label>"` (StaticNav), so
  // top-nav dashboards keep matching (P1D-5530); desktop's old header sent no label.
  const track = (action: string, label: unknown = "") =>
    gtagEventEmitter({
      action,
      category: COMPONENTS_CATEGORY.TOP_NAV,
      new_user_website: true,
      ...(isDesktop ? {} : { label: `${PAGE.SEARCH} ${label}` }),
    });

  // Close on outside click and on Escape.
  useEffect(() => {
    // the phone's sheet sits in a portal, outside this wrapper, and closes itself
    if ((!menuOpen || !isDesktop) && !supportOpen && !productsOpen) return undefined;
    const close = () => {
      setMenuOpen(false);
      setSupportOpen(false);
      setProductsOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, supportOpen, productsOpen, isDesktop]);

  const toggleMenu = () => {
    setSupportOpen(false);
    setMenuOpen((o) => {
      if (!o) {
        import("@Pages/HomeV3/components/LoginBody").catch(() => undefined);
      }
      return !o;
    });
    track(EVENT_LABEL.MENU_CLICKED);
  };

  const signIn = () => {
    setMenuOpen(false);
    track(EVENT_LABEL.LOGIN_CLICKED);
    window.requestAnimationFrame(() => {
      new LoginDispatcher(dispatch).showLoginModal({});
    });
  };

  const logOut = async () => {
    track(EVENT_LABEL.LOGOUT_CLICKED);
    const { data } = await logoutRequest();
    if (data) window.location.reload();
  };

  const open = (which: "preferences" | "app", action: string) => {
    setMenuOpen(false);
    setModal(which);
    track(action);
  };

  // Who is signed in, at the top of the menu: the photo, else an illustrated avatar for the
  // account's stated gender, else initials; the full name; and
  // how the account reaches them — the email, else the phone.
  const account = userData?.data;
  const accountName = nameOf(account);
  const accountContact = account?.email || phoneOf(account?.phone_details, account?.phone);
  const accountAvatar = avatarOf(account);
  const accountHeader = (
    <div className={styles.account}>
      {accountAvatar ? (
        <Image
          src={accountAvatar}
          alt=""
          width="44"
          height="44"
          className={styles.accountAvatar}
          isNotLazy
        />
      ) : (
        <span className={styles.accountAvatar} aria-hidden="true">
          {initialsOf(accountName) || "?"}
        </span>
      )}
      <span className={styles.accountText}>
        <span className={styles.accountName}>{accountName || t("Your account")}</span>
        {accountContact && <span className={styles.accountContact}>{accountContact}</span>}
      </span>
    </div>
  );

  // THE PHONE MENU: every row of the live site's phone menu (Header/DropDownMenu), grouped
  // and drawn with this page's menu rows and animated icons — your account (Profile,
  // Bookings, Shortlist, and Dashboard for internal users; Login is the sheet's foot);
  // book & earn (Group Booking, Refer a Friend); help (Support, the phone's Support page,
  // as its tab); for property owners (List with Us, Partner with Us); app & settings
  // (Download App, preferences, Log out).
  const phoneMenuRows = (
    <>
      {isUserLoggedIn && accountHeader}
      <CustomLink
        href={getLocalizedPath(PROFILE_URL)}
        className={styles.item}
        onClick={() => track("profile_clicked")}
      >
        <RowIcon data={menuIcons?.profile} />
        {t("home.herosection.header.profile")}
      </CustomLink>
      <CustomLink
        href={getLocalizedPath(PROFILE_LEADS_URL)}
        className={styles.item}
        onClick={() => track("bookings_clicked")}
      >
        <RowIcon data={menuIcons?.bookings} />
        {t("home.herosection.header.bookings")}
      </CustomLink>
      <CustomLink
        href={getLocalizedPath(PROFILE_SHORTLIST_URL)}
        className={styles.item}
        onClick={() => track("shortlist_clicked", region)}
      >
        <RowIcon data={menuIcons?.shortlist} />
        {t("home.herosection.header.shortlist")}
        {shortlistCount > 0 && <span className={styles.count}>{shortlistCount}</span>}
      </CustomLink>
      {isInternalUser(userData) && (
        <CustomLink href={DASHBOARD_URL} className={styles.item}>
          <RowIcon data={menuIcons?.bookings} />
          {copy("list.header.dashboard", "Dashboard")}
        </CustomLink>
      )}

      <hr className={styles.rule} />
      <p className={styles.groupLabel}>{copy("home.v3.nav.menu.bookandearn", "Book & earn")}</p>
      <CustomLink
        href={GROUP_BOOKINGS_URL}
        className={styles.item}
        onClick={() => track(EVENT_LABEL.GROUP_BOOKINGS_CLICKED)}
      >
        <RowIcon data={menuIcons?.group} />
        {t("home.herosection.header.groupbooking")}
        <span className={styles.badge}>{t("home.herosection.header.new")}</span>
      </CustomLink>
      <CustomLink
        href={getLocalizedPath(REFER_URL)}
        className={`${styles.item} ${styles.itemRefer}`}
        onClick={() => track(EVENT_LABEL.REFER_CLICKED)}
      >
        <span className={styles.gift}>
          <RowIcon data={menuIcons?.refer} />
          <span className={styles.sparkles} aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </span>
        {t("home.herosection.header.referafriend")}
        <span className={`${styles.badge} ${styles.badgeShimmer}`}>{`${t(
          "home.herosection.header.get",
        )} ${referBonus}`}</span>
      </CustomLink>

      <hr className={styles.rule} />
      <p className={styles.groupLabel}>{copy("Help", "Help")}</p>
      <CustomLink
        href={getLocalizedPath("/profile/support")}
        className={styles.item}
        onClick={() => track(EVENT_LABEL.SUPPORT_CLICKED)}
      >
        <RowIcon data={menuIcons?.help} />
        {t("home.herosection.header.support")}
      </CustomLink>

      {!hidePartnerMenuTiles && (
        <>
          <hr className={styles.rule} />
          <p className={styles.groupLabel}>
            {copy("home.v3.nav.menu.propertyowners", "For property owners")}
          </p>
          <CustomLink
            href={getLocalizedPath(LEADS_LIST)}
            className={styles.item}
            onClick={() => track("list_with_us_clicked")}
          >
            <Image
              src={listIllustration}
              alt=""
              width="22"
              height="22"
              className={styles.rowArt}
              isNotLazy
            />
            {t("home.herosection.header.listwithus")}
          </CustomLink>
          <CustomLink
            href={PARTNER_WITH_AMBER_URL}
            className={styles.item}
            onClick={() => track("partner_with_us_clicked")}
          >
            <Image
              src={partnerIllustration}
              alt=""
              width="22"
              height="22"
              className={styles.rowArt}
              isNotLazy
            />
            {t("home.herosection.header.partnerwithus")}
          </CustomLink>
        </>
      )}

      <hr className={styles.rule} />
      <p className={styles.groupLabel}>
        {copy("home.v3.nav.menu.appandsettings", "App & settings")}
      </p>
      {showAppDownload && (
        <button
          type="button"
          className={styles.item}
          onClick={() => open("app", "download_app_clicked")}
        >
          <RowIcon data={iconDownload} />
          {t("home.herosection.header.downloadapp")}
        </button>
      )}
      <button
        type="button"
        className={styles.item}
        onClick={() => open("preferences", languageEvent)}
      >
        <RowIcon data={menuIcons?.language} />
        {t("Select Preferences")} {languageName}
      </button>
      {isUserLoggedIn && (
        <button type="button" className={`${styles.item} ${styles.itemLogout}`} onClick={logOut}>
          <RowIcon data={menuIcons?.logout} />
          {t("home.herosection.header.logout")}
        </button>
      )}
    </>
  );

  // The phone menu's foot: "Login", as a full-width pill in the primary colour.
  const phoneMenuFoot = isUserLoggedIn ? null : (
    <button type="button" className={styles.loginPill} onClick={signIn}>
      {t("home.herosection.header.login")}
    </button>
  );

  // The desktop panel's rows.
  const menuRows = (
    <>
      {isUserLoggedIn ? (
        accountHeader
      ) : (
        <button type="button" className={`${styles.item} ${styles.itemPrimary}`} onClick={signIn}>
          <RowIcon data={menuIcons?.signin} />
          {t("Sign in or sign up")}
        </button>
      )}

      <hr className={styles.rule} />

      {/* Your account — the same three the site's account menu opens with. Signed out,
            they still link: those pages send the visitor through login first. */}
      <CustomLink
        href={getLocalizedPath(PROFILE_URL)}
        className={styles.item}
        onClick={() => track("profile_clicked")}
      >
        <RowIcon data={menuIcons?.profile} />
        {t("home.herosection.header.profile")}
      </CustomLink>
      <CustomLink
        href={getLocalizedPath(PROFILE_LEADS_URL)}
        className={styles.item}
        onClick={() => track("bookings_clicked")}
      >
        <RowIcon data={menuIcons?.bookings} />
        {t("home.herosection.header.bookings")}
      </CustomLink>
      <CustomLink
        href={getLocalizedPath(PROFILE_SHORTLIST_URL)}
        className={styles.item}
        onClick={() => track("shortlist_clicked", region)}
      >
        <RowIcon data={menuIcons?.shortlist} />
        {t("home.herosection.header.shortlist")}
        {shortlistCount > 0 && <span className={styles.count}>{shortlistCount}</span>}
      </CustomLink>

      <hr className={styles.rule} />

      {/* For students — booking together, earning, and the app. */}
      <CustomLink
        href={GROUP_BOOKINGS_URL}
        className={styles.item}
        onClick={() => track(EVENT_LABEL.GROUP_BOOKINGS_CLICKED)}
      >
        <RowIcon data={menuIcons?.group} />
        {t("home.herosection.header.groupbooking")}
        <span className={styles.badge}>{t("home.herosection.header.new")}</span>
      </CustomLink>
      {/* The one row that earns the student money, so it is the one that stands out:
            a soft green wash, the gift that shakes open (with a few sparkles) as the menu
            opens and again on hover, and a light that sweeps across the reward. */}
      <CustomLink
        href={getLocalizedPath(REFER_URL)}
        className={`${styles.item} ${styles.itemRefer}`}
        onClick={() => track(EVENT_LABEL.REFER_CLICKED)}
      >
        <span className={styles.gift}>
          <RowIcon data={menuIcons?.refer} />
          <span className={styles.sparkles} aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </span>
        {t("home.herosection.header.referafriend")}
        <span className={`${styles.badge} ${styles.badgeShimmer}`}>{`${t(
          "home.herosection.header.get",
        )} ${referBonus}`}</span>
      </CustomLink>

      {/* Phones only: desktop has the nav's own "Get the app" button. */}
      {showAppDownload && (
        <button
          type="button"
          className={`${styles.item} ${styles.itemPhoneOnly}`}
          onClick={() => open("app", "download_app_clicked")}
        >
          <RowIcon data={iconDownload} />
          {t("home.herosection.header.downloadapp")}
        </button>
      )}

      <hr className={styles.rule} />

      {/* Settings and help. */}
      <button
        type="button"
        className={styles.item}
        onClick={() => open("preferences", languageEvent)}
      >
        <RowIcon data={menuIcons?.language} />
        {/* .cn has no currency choice (see PreferencesModal) */}
        {config.IS_CHINA_APP ? t("Language") : t("Languages & currency")}
      </button>
      <CustomLink
        href={getLocalizedPath("/help")}
        // `/help` is a server redirect to the help centre, not a route in this app: a
        // client-side navigation rendered our 404. A full page load reaches the redirect.
        isExternal
        className={styles.item}
        onClick={() => track(EVENT_LABEL.SUPPORT_CLICKED)}
      >
        <RowIcon data={menuIcons?.help} />
        {t("Help Centre")}
      </CustomLink>

      {!hidePartnerMenuTiles && (
        <>
          <hr className={styles.rule} />

          {/* For property owners and partners: two compact tiles side by side, so the
            menu stays short enough for a short window. */}
          <div className={styles.featureRow}>
            <CustomLink
              href={getLocalizedPath(LEADS_LIST)}
              className={styles.feature}
              onClick={() => track("list_with_us_clicked")}
            >
              <Image
                src={listIllustration}
                alt=""
                width="36"
                height="36"
                className={styles.featureArt}
                isNotLazy
              />
              <span className={styles.featureTitle}>
                {t("home.herosection.header.listyourproperty")}
              </span>
              <span className={styles.featureSub}>
                {copy("home.v3.nav.menu.reachuniversities", "Reach 800+ universities")}
              </span>
            </CustomLink>
            <CustomLink
              href={PARTNER_WITH_AMBER_URL}
              className={styles.feature}
              onClick={() => track("partner_with_us_clicked")}
            >
              <Image
                src={partnerIllustration}
                alt=""
                width="36"
                height="36"
                className={styles.featureArt}
                isNotLazy
              />
              <span className={styles.featureTitle}>
                {copy("home.listandpartnerwithus.partnerwithus", "Partner with us")}
              </span>
              <span className={styles.featureSub}>
                {copy("home.v3.nav.menu.partnersub", "Unis, agencies & brands")}
              </span>
            </CustomLink>
          </div>
        </>
      )}

      {isUserLoggedIn && (
        <>
          <hr className={styles.rule} />
          <button type="button" className={`${styles.item} ${styles.itemLogout}`} onClick={logOut}>
            <RowIcon data={menuIcons?.logout} />
            {t("home.herosection.header.logout")}
          </button>
        </>
      )}
    </>
  );

  return (
    <div className={`${styles.wrap} ${tone === "light" ? styles.light : ""}`} ref={wrapRef}>
      {/* "Get the app": the same scan-or-download modal as the menu's Download App row. */}
      {showProducts && (
        <button
          type="button"
          className={`${styles.link} ${styles.glass}`}
          aria-expanded={productsOpen}
          aria-haspopup="menu"
          data-testid="topnavbar-products"
          onClick={() => {
            setMenuOpen(false);
            setSupportOpen(false);
            setProductsOpen((o) => !o);
            gtagEventEmitter({
              action: "products_clicked",
              category: COMPONENTS_CATEGORY.TOP_NAV,
              new_user_website: true,
            });
          }}
        >
          Products
          <Chevron height="16" width="16" rotate={productsOpen ? "180" : ""} />
        </button>
      )}
      {showProducts && productsOpen && <ProductsMenu onClose={() => setProductsOpen(false)} />}
      {showAppDownload && !showProducts && (
        <button
          type="button"
          className={`${styles.link} ${styles.glass}`}
          onClick={() => open("app", "download_app_clicked")}
          data-testid="topnavbar-download-app"
        >
          <NavIcon data={iconDownload} />
          {copy("home.footer.gettheapp", "Get the app")}
        </button>
      )}
      {/* Locale pill: translate glyph, language code, and currency symbol when set. */}
      <button
        type="button"
        className={`${styles.link} ${styles.glass} ${styles.locale}`}
        onClick={() => open("preferences", languageEvent)}
        aria-label={`${languageName}. ${
          config.IS_CHINA_APP ? t("Language") : t("Languages & currency")
        }`}
        data-testid="topnavbar-language-currency"
      >
        <span className={styles.localeLang}>
          <NavIcon data={navTranslate} />
          {languageCode}
        </span>
        {!config.IS_CHINA_APP && userCurrencyCode ? (
          <>
            <span className={styles.localeSep} aria-hidden="true" />
            <span className={styles.localeCurrency}>{userCurrencyCode}</span>
          </>
        ) : null}
      </button>
      {/* "Support" (was "Help", a link to the Help Centre): opens the live header's
        Support menu — chat, WhatsApp, Messenger, call, email, and the Help Centre. */}
      <button
        type="button"
        className={`${styles.link} ${styles.glass}`}
        aria-expanded={supportOpen}
        aria-haspopup="menu"
        data-testid="topnavbar-support"
        onClick={() => {
          setMenuOpen(false);
          setSupportOpen((o) => !o);
          track(EVENT_LABEL.SUPPORT_CLICKED);
        }}
      >
        <SupportIcon />
        {copy("home.herosection.header.support", "Support")}
      </button>
      {supportOpen && (
        <SupportMenu
          onClose={() => setSupportOpen(false)}
          onCallUs={() => {
            setSupportOpen(false);
            setCallUsOpen(true);
          }}
        />
      )}
      {callUsOpen && <CallUsDrawer country={country} onClose={() => setCallUsOpen(false)} />}

      {/* The menu: sign in and the account links live inside it. */}
      <button
        type="button"
        className={styles.roundButton}
        aria-label={
          menuOpen
            ? copy("navbar.v2.menu.close", "Close menu")
            : copy("navbar.v2.menu.open", "Open menu")
        }
        aria-expanded={menuOpen}
        onClick={toggleMenu}
        onMouseEnter={loadMenuIcons}
        onFocus={loadMenuIcons}
        data-testid="topnavbar-hamburger-menu"
      >
        <MenuGlyph open={menuOpen} />
      </button>

      {/* One menu, two frames: the desktop's dropdown panel, and on a phone the same
        rows — the same animated icons, tags and tiles — in an iOS sheet (MenuSheet). */}
      {menuOpen && isDesktop && (
        <div className={styles.menu} role="menu">
          {menuRows}
        </div>
      )}
      {menuOpen && !isDesktop && (
        <MenuSheet onClose={() => setMenuOpen(false)} footer={phoneMenuFoot}>
          <div className={styles.sheetMenu} role="menu">
            {phoneMenuRows}
          </div>
        </MenuSheet>
      )}

      {modal === "preferences" && (
        <Portal>
          <Modal size="fitContent" align="center" hideModal={() => setModal("")}>
            <PreferencesModal onClose={() => setModal("")} />
          </Modal>
        </Portal>
      )}
      {modal === "app" && (
        <Portal>
          <AppDownloadModal
            onClose={() => setModal("")}
            onStoreClick={(store) => track(`header_app_modal_${store}`)}
          />
        </Portal>
      )}
    </div>
  );
};

export default wrapperHOC(HomeV2HeroNav, {
  componentName: "HeroNav-HomeV2",
  showForChina: true,
  showForWhiteLable: false,
});
